import { UNITS, IMPERIAL, METRIC, SYSTEMS_OF_MEASURE } from '../constants/uom';
import { formatRadon } from './formatters';


export class Pressure {
  constructor(value, unit) {
    this.value = value;
    this.unit = unit;
  }

  static get kPaTombar() {
    return 10.0;
  }

  static get mbarTokPa() {
    return 1 / Pressure.kPaTombar;
  }

  toMetric() {
    if (this.unit === UNITS.pressure.mbar) {
      return this.value;
    }
    return this.value * Pressure.kPaTombar;
  }

  toImperial() {
    if (this.unit === UNITS.pressure.kPa) {
      return this.value;
    }
    return this.value * Pressure.mbarTokPa;
  }
}

export class Radon {
  constructor(value, unit) {
    this.value = value;
    this.unit = unit;
  }

  static get picoCurieToBecquerel() {
    return 0.037;
  }

  static get becquerelToPicoCurie() {
    return 1 / Radon.picoCurieToBecquerel;
  }

  static get meterCubicToLiter() {
    return 1000;
  }

  static get literToMeterCubic() {
    return 1 / Radon.meterCubicToLiter;
  }

  static get picoCuriePerLiterToBecquerelPerMeterCubic() {
    return Radon.picoCurieToBecquerel / Radon.literToMeterCubic; // 37
  }

  static get becquerelPerMeterCubicToPicoCuriePerLiter() {
    return Radon.becquerelToPicoCurie / Radon.meterCubicToLiter; // 1/37
  }

  toMetric() {
    if (this.unit === UNITS.radon.bqM3) {
      return this.value;
    }
    return this.value * Radon.picoCuriePerLiterToBecquerelPerMeterCubic;
  }

  toImperial() {
    if (this.unit === UNITS.radon.pciL) {
      return this.value;
    }
    return this.value * Radon.becquerelPerMeterCubicToPicoCuriePerLiter;
  }

  toUnitOfMeasure(uom) {
    if (uom === IMPERIAL ) {
      return this.toImperial();
    }

    if (uom === METRIC) {
      return this.toMetric();
    }

    return null;
  }
}

export class Temperature {
  constructor(value, unit) {
    this.value = value;
    this.unit = unit;
  }

  static get scaleDiff() {
    return 32;
  }

  static get ftoc() {
    return 5.0 / 9.0;
  }

  static get ctof() {
    return 1 / Temperature.ftoc;
  }

  toMetric() {
    if (this.unit === UNITS.temperature.celsius) {
      return this.value;
    }
    return (this.value - Temperature.scaleDiff) * Temperature.ftoc;
  }

  toImperial() {
    if (this.unit === UNITS.temperature.fahrenheit) {
      return this.value;
    }
    return this.value * Temperature.ctof + Temperature.scaleDiff;
  }
}

export const getMDCAverageRadonValue = (test, uom, deviceTypes) => {
  const {
    averageRadon,
    deviceType
  } = test;
  const edgeLevels = {};
  edgeLevels[deviceTypes.RS300] = 0.5;
  edgeLevels[deviceTypes.RS800] = 0.3;
  edgeLevels[deviceTypes.ALPHA310] = 0.4;
  edgeLevels[deviceTypes.ALPHA516] = 0.3;
  edgeLevels[deviceTypes.ALPHA830] = 0.2;

  const radon = new Radon(averageRadon, SYSTEMS_OF_MEASURE[uom].radon);
  const imperialValue = radon.toImperial();
  const originalValue = formatRadon(averageRadon, uom);

  if (deviceType === deviceTypes.SN1027) {
    return originalValue;
  }

  const edgeLevel = edgeLevels[deviceType] || 0.5;
  const edgeRadonLevel = new Radon(edgeLevel, UNITS.radon.pciL);

  return imperialValue < edgeRadonLevel.toImperial()
    ? `< ${formatRadon(edgeRadonLevel.toUnitOfMeasure(uom), uom)}`
    : originalValue;
}


// WEBPACK FOOTER //
// src/utils/uom.js